import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
    Modal,
    Form,
    Input,
    Select,
    Button,
    Collapse,
    Space,
    Typography,
    message,
    Spin,
    Divider,
    Tabs,
    Empty,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined, TableOutlined } from '@ant-design/icons';
import type {
    EvalGroundTruthRequest,
    EvalGroundTruthResponse,
    EvalGroundTruthUpdateRequest,
    EvalFromChatDraft,
    GroundTruthPlanItem,
    GroundTruthPerTaskItem,
    GroundTruthVizItem,
    TaskResultSqlOutput,
    QuestionSetInfo,
    GroundTruthComment,
} from './evalTypes';
import {
    INTENT_OPTIONS,
    STATUS_OPTIONS,
    CHART_TYPE_OPTIONS,
    type GroundTruthMetadata,
} from './evalTypes';
import type { Tenant, FileEntry } from '../prompts/types';
import { parseTableData } from './parseTableData';
import SkillStudioVizPreview from './SkillStudioVizPreview';
import SkillStudioEditableTable from './SkillStudioEditableTable';

const { TextArea } = Input;
const { Text } = Typography;

export type FormMode = 'create' | 'edit' | 'from_chat';

export interface SkillStudioEvalGroundTruthSubmitPayload {
    mode: FormMode;
    entryId?: string;
    body: EvalGroundTruthRequest | EvalGroundTruthUpdateRequest;
    metadata?: GroundTruthMetadata;
}

export interface SkillStudioEvalGroundTruthFormProps {
    open: boolean;
    mode: FormMode;
    initialData?: EvalFromChatDraft | EvalGroundTruthResponse | null;
    entryId?: string;
    onClose: () => void;
    onSaved?: (entry: EvalGroundTruthResponse) => void;
    questionSets: QuestionSetInfo[];
    tenants: Tenant[];
    taskFileEntries: FileEntry[];
    onTenantChange: (tenant: string) => void;
    saving?: boolean;
    onSubmit: (payload: SkillStudioEvalGroundTruthSubmitPayload) => Promise<EvalGroundTruthResponse>;
    onAddComment?: (entryId: string, text: string) => Promise<EvalGroundTruthResponse>;
}

// --- Per-task output UI state ---

interface PerTaskUIState {
    task: string;
    subQuery: string;
    sqlQueries: string[];
    sqlTables: TaskResultSqlOutput[];
    pasteTexts: string[];
    narrative: string;
    keyTakeaways: string;
    assumptions: string;
    formulae: string;
    chartType: string;
    chartTitle: string;
    /** Chart viz object (non-table) — for Visualization tab */
    vizRaw: GroundTruthVizItem | null;
    /** Table viz object — for Table tab alongside sql_db_output */
    tableVizRaw: GroundTruthVizItem | null;
}

function emptyPerTask(task = '', subQuery = ''): PerTaskUIState {
    return {
        task, subQuery,
        sqlQueries: [], sqlTables: [], pasteTexts: [],
        narrative: '', keyTakeaways: '', assumptions: '', formulae: '',
        chartType: '', chartTitle: '',
        vizRaw: null, tableVizRaw: null,
    };
}

/**
 * Normalize sql_db_output: if rows are 2D arrays (standard SQL format),
 * convert them to column-keyed objects so Ant Design Table can render them.
 */
function normalizeSqlOutput(tables: TaskResultSqlOutput[]): TaskResultSqlOutput[] {
    return tables.map((t) => {
        if (!t.rows || t.rows.length === 0 || !t.columns || t.columns.length === 0) return t;
        const firstRow = t.rows[0];
        if (Array.isArray(firstRow)) {
            return {
                columns: t.columns,
                rows: (t.rows as unknown as unknown[][]).map((rowArr) => {
                    const obj: Record<string, unknown> = {};
                    t.columns.forEach((col, idx) => {
                        obj[col] = rowArr[idx] ?? null;
                    });
                    return obj;
                }),
            };
        }
        return t;
    });
}

/**
 * Split viz (single or array) into table-type and chart-type viz objects.
 */
function splitViz(viz: GroundTruthVizItem | GroundTruthVizItem[] | undefined): {
    chartViz: GroundTruthVizItem | null;
    tableViz: GroundTruthVizItem | null;
} {
    if (!viz) return { chartViz: null, tableViz: null };
    const list = Array.isArray(viz) ? viz : [viz];
    const tableViz = list.find((v) => String(v.chart_type ?? '').toLowerCase() === 'table') ?? null;
    const chartViz = list.find((v) => String(v.chart_type ?? '').toLowerCase() !== 'table') ?? null;
    return { chartViz, tableViz };
}

/**
 * Convert a table-type viz (2D data array) into TaskResultSqlOutput.
 */
function tableVizToSqlOutput(viz: GroundTruthVizItem): TaskResultSqlOutput | null {
    const data = (viz as Record<string, unknown>).data;
    if (!Array.isArray(data) || data.length < 2) return null;
    const headers = (data[0] as unknown[]).map(String);
    const rows = data.slice(1).map((row: unknown[]) => {
        const obj: Record<string, unknown> = {};
        headers.forEach((h, i) => { obj[h] = Array.isArray(row) && i < row.length ? row[i] ?? null : null; });
        return obj;
    });
    return { columns: headers, rows };
}

function perTaskItemToUI(item: GroundTruthPerTaskItem): PerTaskUIState {
    const tables = normalizeSqlOutput(item.sql_db_output ?? []);
    const { chartViz, tableViz } = splitViz(item.viz);

    // Merge tableVizRaw into sqlTables so everything is editable in one list
    const allTables = [...tables];
    if (tableViz) {
        const converted = tableVizToSqlOutput(tableViz);
        if (converted && converted.columns.length > 0) allTables.unshift(converted);
    }

    return {
        task: item.task,
        subQuery: item.sub_query ?? '',
        sqlQueries: item.sql_queries ?? [],
        sqlTables: allTables,
        pasteTexts: allTables.map(() => ''),
        narrative: item.narrative?.overview ?? '',
        keyTakeaways: (item.narrative?.key_takeaways ?? []).join('\n'),
        assumptions: (item.narrative?.assumptions ?? []).join('\n'),
        formulae: (item.narrative?.formulae ?? []).join('\n'),
        chartType: chartViz?.chart_type ?? '',
        chartTitle: chartViz?.chartTitle ?? '',
        vizRaw: chartViz,
        tableVizRaw: null,
    };
}

function uiToPerTaskItem(ui: PerTaskUIState): GroundTruthPerTaskItem {
    const item: GroundTruthPerTaskItem = { task: ui.task };
    if (ui.subQuery) item.sub_query = ui.subQuery;
    const queries = ui.sqlQueries.filter((q) => q.trim());
    if (queries.length) item.sql_queries = queries;
    const tables = ui.sqlTables.filter((t) => t.columns?.length > 0 && t.rows?.length > 0);
    if (tables.length) item.sql_db_output = tables;
    if (ui.chartType || ui.vizRaw) {
        item.viz = {
            ...(ui.vizRaw ?? { chart_type: '', chartTitle: '' }),
            chart_type: ui.chartType || ui.vizRaw?.chart_type || '',
            chartTitle: ui.chartTitle || ui.vizRaw?.chartTitle || '',
        };
    }
    const overview = ui.narrative.trim();
    const takeaways = ui.keyTakeaways.split('\n').map((s) => s.trim()).filter(Boolean);
    const assumptions = ui.assumptions.split('\n').map((s) => s.trim()).filter(Boolean);
    const formulae = ui.formulae.split('\n').map((s) => s.trim()).filter(Boolean);
    if (overview || takeaways.length || assumptions.length || formulae.length) {
        item.narrative = {};
        if (overview) item.narrative.overview = overview;
        if (takeaways.length) item.narrative.key_takeaways = takeaways;
        if (assumptions.length) item.narrative.assumptions = assumptions;
        if (formulae.length) item.narrative.formulae = formulae;
    }
    return item;
}

function formatCommentTimestamp(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

// ---- Component ----

const SkillStudioEvalGroundTruthForm: React.FC<SkillStudioEvalGroundTruthFormProps> = ({
    open,
    mode,
    initialData,
    entryId,
    onClose,
    onSaved,
    questionSets,
    tenants,
    taskFileEntries,
    onTenantChange,
    saving: savingProp,
    onSubmit,
    onAddComment,
}) => {
    const [form] = Form.useForm();
    const [savingLocal, setSavingLocal] = useState(false);
    const saving = savingProp ?? savingLocal;
    const [commentText, setCommentText] = useState('');
    const [addingComment, setAddingComment] = useState(false);

    const [selectedTenant, setSelectedTenant] = useState<string>('');

    // Per-task outputs (separate from Ant Form)
    const [perTaskList, setPerTaskList] = useState<PerTaskUIState[]>([]);

    // --- Populate form from initialData ---
    useEffect(() => {
        if (open && initialData) {
            const gt = initialData.ground_truth ?? {};
            form.setFieldsValue({
                question_set: initialData.question_set ? [initialData.question_set] : [],
                question: initialData.question,
                intent: gt.GroundTruthIntent,
                refine: gt.GroundTruthRefine,
                tasks: gt.GroundTruthTasks ?? [],
                plan: (gt.GroundTruthPlan ?? []).map((p) => ({
                    task: p.task || undefined,
                    sub_query: p.sub_query,
                })),
                summary: gt.GroundTruthSummary,
                status: initialData.status ?? 'draft',
            });
            const planSteps = gt.GroundTruthPlan ?? [];
            setPerTaskList((gt.GroundTruthPerTask ?? []).map((item) => {
                const ui = perTaskItemToUI(item);
                if (!ui.subQuery && ui.task) {
                    const match = planSteps.find((p) => p.task === ui.task);
                    if (match?.sub_query) ui.subQuery = match.sub_query;
                }
                return ui;
            }));
            setCommentText('');
        } else if (open) {
            form.resetFields();
            form.setFieldsValue({ status: 'draft', plan: [], tasks: [] });
            setPerTaskList([]);
            setCommentText('');
        }
    }, [open, initialData, form]);

    const taskNameOptions = useMemo(
        () => taskFileEntries.map((f) => {
            const raw = f.filename || f.file_id;
            const label = raw.replace(/\.ya?ml$/i, '');
            return { value: label, label };
        }),
        [taskFileEntries],
    );

    // --- Per-task helpers ---

    const updatePerTask = useCallback((idx: number, patch: Partial<PerTaskUIState>) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            next[idx] = { ...(next[idx] ?? emptyPerTask()), ...patch };
            return next;
        });
    }, []);

    const addPerTask = useCallback(() => {
        setPerTaskList((prev) => [...prev, emptyPerTask()]);
    }, []);

    const removePerTask = useCallback((idx: number) => {
        setPerTaskList((prev) => prev.filter((_, i) => i !== idx));
    }, []);

    const handleAddTable = useCallback((taskIdx: number) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            t.sqlTables = [...t.sqlTables, { columns: [], rows: [] }];
            t.pasteTexts = [...t.pasteTexts, ''];
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleRemoveTable = useCallback((taskIdx: number, tblIdx: number) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            t.sqlTables = t.sqlTables.filter((_, i) => i !== tblIdx);
            t.pasteTexts = t.pasteTexts.filter((_, i) => i !== tblIdx);
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handlePasteTextChange = useCallback((taskIdx: number, tblIdx: number, val: string) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            const texts = [...t.pasteTexts];
            texts[tblIdx] = val;
            t.pasteTexts = texts;
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleParseTable = useCallback((taskIdx: number, tblIdx: number, text: string) => {
        const parsed = parseTableData(text);
        if (!parsed) { message.warning('Could not parse table data — check format'); return; }
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            const tables = [...t.sqlTables];
            tables[tblIdx] = parsed;
            t.sqlTables = tables;
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleTableEdit = useCallback((taskIdx: number, tblIdx: number, updated: TaskResultSqlOutput) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            const tables = [...t.sqlTables];
            tables[tblIdx] = updated;
            t.sqlTables = tables;
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleAddSqlQuery = useCallback((taskIdx: number) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            t.sqlQueries = [...t.sqlQueries, ''];
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleSqlQueryChange = useCallback((taskIdx: number, qIdx: number, val: string) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            const queries = [...t.sqlQueries];
            queries[qIdx] = val;
            t.sqlQueries = queries;
            next[taskIdx] = t;
            return next;
        });
    }, []);

    const handleRemoveSqlQuery = useCallback((taskIdx: number, qIdx: number) => {
        setPerTaskList((prev) => {
            const next = [...prev];
            const t = { ...next[taskIdx] };
            t.sqlQueries = t.sqlQueries.filter((_, i) => i !== qIdx);
            next[taskIdx] = t;
            return next;
        });
    }, []);

    // --- Plan step helpers ---

    const handleAddPlanStep = useCallback((addFn: () => void) => { addFn(); }, []);
    const handleRemovePlanStep = useCallback((removeFn: (idx: number) => void, idx: number) => { removeFn(idx); }, []);

    // --- Save ---

    const handleSave = async () => {
        try {
            const raw = await form.validateFields();
            if (savingProp == null) setSavingLocal(true);

            const questionSet = Array.isArray(raw.question_set) ? raw.question_set[0] : raw.question_set;
            const values = { ...raw, question_set: questionSet };

            const planSteps: GroundTruthPlanItem[] = (values.plan ?? [])
                .map((p: { task?: string; sub_query?: string }) => {
                    if (!p.task && !p.sub_query) return null;
                    return { task: p.task ?? '', sub_query: p.sub_query ?? '' };
                })
                .filter(Boolean) as GroundTruthPlanItem[];

            const perTask = perTaskList
                .filter((t) => t.task.trim())
                .map(uiToPerTaskItem);

            const groundTruth: EvalGroundTruthRequest['ground_truth'] = {
                GroundTruthIntent: values.intent,
                GroundTruthRefine: values.refine,
                GroundTruthTasks: values.tasks,
                GroundTruthPlan: planSteps,
                GroundTruthSummary: values.summary,
                GroundTruthPerTask: perTask.length > 0 ? perTask : undefined,
            };
            const initialComment = !entryId && commentText.trim()
                ? { text: commentText.trim() }
                : undefined;

            const saved = await onSubmit(
                mode === 'edit' && entryId
                    ? {
                        mode,
                        entryId,
                        body: {
                            question_set: values.question_set,
                            question: values.question,
                            ground_truth: groundTruth,
                            status: values.status,
                        },
                    }
                    : {
                        mode,
                        body: {
                            question_set: values.question_set,
                            question: values.question,
                            ground_truth: groundTruth,
                            status: values.status,
                            initial_comment: initialComment,
                        },
                        metadata: initialData?.metadata,
                    },
            );

            message.success(mode === 'edit' ? 'Entry updated' : 'Entry created');
            onSaved?.(saved);
            onClose();
        } catch (err: unknown) {
            if (err && typeof err === 'object' && 'errorFields' in err) return;
            message.error(String(err));
        } finally {
            if (savingProp == null) setSavingLocal(false);
        }
    };

    const title =
        mode === 'from_chat' ? 'Add to Eval Set'
            : mode === 'edit' ? 'Edit Ground Truth'
            : 'New Ground Truth';

    const comments: GroundTruthComment[] = initialData?.comments ?? [];
    const canAddComment = Boolean(entryId && onAddComment);
    const canDraftInitialComment = !entryId;
    const canEditCommentText = canAddComment || canDraftInitialComment;

    const handleAddComment = async () => {
        const trimmed = commentText.trim();
        if (!trimmed || !entryId || !onAddComment) return;
        setAddingComment(true);
        try {
            const updated = await onAddComment(entryId, trimmed);
            setCommentText('');
            message.success('Comment added');
            onSaved?.(updated);
        } catch (err: unknown) {
            message.error(String(err));
        } finally {
            setAddingComment(false);
        }
    };

    const renderCommentsTab = () => {
        const sortedComments = [...comments].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {sortedComments.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {sortedComments.map((comment, idx) => (
                            <div
                                key={`${comment.created_at}-${idx}`}
                                style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 10, background: '#fafafa' }}
                            >
                                <Space size={8} style={{ marginBottom: 4 }} wrap>
                                    <Text strong>{comment.author || 'Unknown'}</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {formatCommentTimestamp(comment.created_at)}
                                    </Text>
                                </Space>
                                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{comment.text}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No comments yet" />
                )}

                <div>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                        {canDraftInitialComment ? 'Initial comment' : 'Add comment'}
                    </Text>
                    <TextArea
                        rows={3}
                        value={commentText}
                        disabled={!canEditCommentText || addingComment}
                        placeholder={canDraftInitialComment ? 'Add a comment to save with this entry' : 'Add subjective feedback or comments'}
                        onChange={(e) => setCommentText(e.target.value)}
                    />
                    {canDraftInitialComment ? (
                        <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                            This comment will be added when you save the Ground Truth entry.
                        </Text>
                    ) : (
                        <Button
                            type="primary"
                            style={{ marginTop: 8 }}
                            loading={addingComment}
                            disabled={!canAddComment || !commentText.trim()}
                            onClick={handleAddComment}
                        >
                            Add comment
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    // --- Render per-task output panel ---

    const renderPerTaskOutputs = (taskIdx: number) => {
        const pt = perTaskList[taskIdx] ?? emptyPerTask();
        return (
            <Tabs
                size="small"
                items={[
                    {
                        key: 'query',
                        label: 'Query',
                        children: (
                            <div>
                                {pt.sqlQueries.map((q, qIdx) => (
                                    <div key={`q-${qIdx}`} style={{ marginBottom: 8 }}>
                                        <Space align="start" style={{ width: '100%' }}>
                                            <Text strong>Query {qIdx + 1}</Text>
                                            <Button size="small" danger onClick={() => handleRemoveSqlQuery(taskIdx, qIdx)}>Remove</Button>
                                        </Space>
                                        <TextArea
                                            rows={2}
                                            value={q}
                                            onChange={(e) => handleSqlQueryChange(taskIdx, qIdx, e.target.value)}
                                            style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}
                                            placeholder="SELECT ... FROM ..."
                                        />
                                    </div>
                                ))}
                                <Button type="dashed" size="small" onClick={() => handleAddSqlQuery(taskIdx)}>
                                    + Add SQL query
                                </Button>
                            </div>
                        ),
                    },
                    {
                        key: 'table',
                        label: 'Table',
                        children: (
                            <div>
                                {pt.sqlTables.map((table, tIdx) => (
                                    <div key={`t-${tIdx}`} style={{ marginBottom: 16 }}>
                                        <Space align="start" style={{ width: '100%', marginBottom: 4 }}>
                                            <Text strong>Table {tIdx + 1}</Text>
                                            <Button size="small" danger onClick={() => handleRemoveTable(taskIdx, tIdx)}>Remove</Button>
                                        </Space>
                                        {table.columns?.length > 0 && table.rows?.length > 0 ? (
                                            <SkillStudioEditableTable
                                                value={table}
                                                onChange={(updated) => handleTableEdit(taskIdx, tIdx, updated)}
                                            />
                                        ) : (
                                            <div>
                                                <TextArea
                                                    rows={3}
                                                    placeholder="Paste tab-separated data from Excel (first row = headers)"
                                                    value={pt.pasteTexts[tIdx] ?? ''}
                                                    onChange={(e) => handlePasteTextChange(taskIdx, tIdx, e.target.value)}
                                                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                                                />
                                                <Button
                                                    size="small"
                                                    icon={<TableOutlined />}
                                                    style={{ marginTop: 4 }}
                                                    onClick={() => handleParseTable(taskIdx, tIdx, pt.pasteTexts[tIdx] ?? '')}
                                                >
                                                    Parse
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => handleAddTable(taskIdx)}>
                                    Add table
                                </Button>
                            </div>
                        ),
                    },
                    {
                        key: 'narrative',
                        label: 'Narrative',
                        children: (
                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>Overview</Text>
                                <TextArea
                                    rows={3}
                                    placeholder="Narrative overview for this task"
                                    value={pt.narrative}
                                    onChange={(e) => updatePerTask(taskIdx, { narrative: e.target.value })}
                                />
                                <Text strong style={{ display: 'block', marginTop: 8, marginBottom: 4 }}>Key Takeaways (one per line)</Text>
                                <TextArea
                                    rows={3}
                                    placeholder="One takeaway per line"
                                    value={pt.keyTakeaways}
                                    onChange={(e) => updatePerTask(taskIdx, { keyTakeaways: e.target.value })}
                                />
                            </div>
                        ),
                    },
                    {
                        key: 'assumptions',
                        label: 'Assumptions',
                        children: (
                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>Assumptions (one per line)</Text>
                                <TextArea
                                    rows={5}
                                    placeholder="One assumption per line"
                                    value={pt.assumptions}
                                    onChange={(e) => updatePerTask(taskIdx, { assumptions: e.target.value })}
                                />
                            </div>
                        ),
                    },
                    {
                        key: 'formulae',
                        label: 'Formulae',
                        children: (
                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>Formulae (one per line)</Text>
                                <TextArea
                                    rows={5}
                                    placeholder="One formula per line"
                                    value={pt.formulae}
                                    onChange={(e) => updatePerTask(taskIdx, { formulae: e.target.value })}
                                />
                            </div>
                        ),
                    },
                    {
                        key: 'viz',
                        label: 'Visualization',
                        children: (
                            <div>
                                <Space style={{ marginBottom: 8 }}>
                                    <Select
                                        allowClear
                                        placeholder="Chart type"
                                        style={{ width: 180 }}
                                        value={pt.chartType || undefined}
                                        onChange={(val) => updatePerTask(taskIdx, { chartType: val ?? '' })}
                                        options={CHART_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                                    />
                                    <Input
                                        placeholder="Chart title"
                                        style={{ width: 260 }}
                                        value={pt.chartTitle}
                                        onChange={(e) => updatePerTask(taskIdx, { chartTitle: e.target.value })}
                                    />
                                </Space>
                                {(pt.vizRaw || pt.chartType) && (
                                    <div style={{ marginTop: 8 }}>
                                        <SkillStudioVizPreview
                                            chartType={pt.chartType}
                                            chartTitle={pt.chartTitle}
                                            vizData={pt.vizRaw ?? undefined}
                                            sqlOutput={pt.sqlTables[0]}
                                        />
                                    </div>
                                )}
                            </div>
                        ),
                    },
                ]}
            />
        );
    };

    return (
        <Modal
            title={title}
            open={open}
            onCancel={onClose}
            width={900}
            destroyOnClose
            footer={
                <Space>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
                </Space>
            }
        >
            <Spin spinning={saving}>
                <Form form={form} layout="vertical" requiredMark="optional" style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
                    <Tabs
                        items={[
                            {
                                key: 'ground-truth',
                                label: 'Ground Truth',
                                children: (
                                    <>
                    <Form.Item
                        name="question_set"
                        label="Question Set"
                        rules={[{
                            required: true,
                            message: 'Required',
                            validator: (_, value) =>
                                Array.isArray(value) && value.length > 0
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Required')),
                        }]}
                    >
                        <Select
                            showSearch allowClear mode="tags" maxCount={1}
                            placeholder="Select or type a new set name"
                            options={questionSets.map((s) => ({ value: s.question_set, label: `${s.question_set} (${s.count})` }))}
                            filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                        />
                    </Form.Item>

                    <Form.Item name="question" label="Question" rules={[{ required: true, message: 'Required' }]}>
                        <TextArea rows={2} />
                    </Form.Item>

                    <Form.Item name="intent" label="Intent">
                        <Select allowClear placeholder="Select intent" options={INTENT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
                    </Form.Item>

                    <Form.Item name="refine" label="Refine Output">
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="tasks" label="Tasks">
                        <Select mode="tags" placeholder="Add tasks (e.g. data_exploration)" />
                    </Form.Item>

                    {/* Plan: task + sub_query pairs */}
                    <Divider orientation="left" plain>Plan</Divider>

                    <div style={{ marginBottom: 8 }}>
                        <Select
                            allowClear showSearch
                            placeholder="Select branch to load task names"
                            style={{ width: 300 }}
                            value={selectedTenant || undefined}
                            onChange={(val) => {
                                const tenant = val ?? '';
                                setSelectedTenant(tenant);
                                onTenantChange(tenant);
                            }}
                            options={tenants.map((t) => ({ value: t.name, label: t.name }))}
                        />
                    </div>

                    <Form.List name="plan">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...rest }) => (
                                    <Space key={key} align="start" style={{ display: 'flex', marginBottom: 4 }}>
                                        <Form.Item {...rest} name={[name, 'task']} style={{ marginBottom: 0, minWidth: 220 }}>
                                            <Select
                                                showSearch allowClear
                                                placeholder="Task name" style={{ width: 220 }}
                                                options={taskNameOptions}
                                                filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                                            />
                                        </Form.Item>
                                        <Form.Item {...rest} name={[name, 'sub_query']} style={{ marginBottom: 0 }}>
                                            <Input placeholder="Sub-query" style={{ width: 420 }} />
                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => handleRemovePlanStep(remove, name)} />
                                    </Space>
                                ))}
                                <Button type="dashed" onClick={() => handleAddPlanStep(add)} icon={<PlusOutlined />} style={{ marginBottom: 16 }}>
                                    Add plan step
                                </Button>
                            </>
                        )}
                    </Form.List>

                    <Form.Item name="summary" label="Summary">
                        <TextArea rows={6} />
                    </Form.Item>

                    <Form.Item name="status" label="Status">
                        <Select options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
                    </Form.Item>

                    {/* Per-task outputs section */}
                    <Divider orientation="left" plain>Per-Task Outputs</Divider>

                    <Collapse
                        size="small"
                        accordion
                        items={perTaskList.map((pt, idx) => ({
                            key: String(idx),
                            label: (
                                <Space>
                                    <span>{pt.task || `Task ${idx + 1}`}{pt.subQuery ? ` — ${pt.subQuery}` : ''}</span>
                                    <MinusCircleOutlined
                                        onClick={(e) => { e.stopPropagation(); removePerTask(idx); }}
                                        style={{ color: '#ff4d4f' }}
                                    />
                                </Space>
                            ),
                            children: (
                                <div>
                                    <Space style={{ marginBottom: 8 }}>
                                        <Select
                                            showSearch allowClear
                                            placeholder="Task name" style={{ width: 220 }}
                                            value={pt.task || undefined}
                                            onChange={(val) => updatePerTask(idx, { task: val ?? '' })}
                                            options={taskNameOptions}
                                            filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                                        />
                                        <Input
                                            placeholder="Sub-query"
                                            style={{ width: 360 }}
                                            value={pt.subQuery}
                                            onChange={(e) => updatePerTask(idx, { subQuery: e.target.value })}
                                        />
                                    </Space>
                                    {renderPerTaskOutputs(idx)}
                                </div>
                            ),
                        }))}
                    />
                    <Button type="dashed" onClick={addPerTask} icon={<PlusOutlined />} style={{ marginTop: 8, marginBottom: 16 }}>
                        Add task output
                    </Button>
                                    </>
                                ),
                            },
                            {
                                key: 'comments',
                                label: 'Subjective Feedback / Comments',
                                children: renderCommentsTab(),
                            },
                        ]}
                    />
                </Form>
            </Spin>
        </Modal>
    );
};

export default SkillStudioEvalGroundTruthForm;
